import React, {memo} from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'

/* Cosmos */
import Heart from '@cosmos/heart'

/* Constants */
import {DAVINCI_EVENT_NAMES} from '../../../../modules/analytics/davinci/davinciConstants'

/* Utils */
import {generateProductStatus} from '../../../../modules/home/homeUtils'
import {generateUrlForGoogleBot} from '../../../../utils/urlHelper'

/* Components */
import ProductImage from './ProductImage'
import ProductInfo from './ProductInfo'

/* Styles */
import s from './product-card.scss'

const ProductCard = (props) => {
  const {
    imageUrl,
    name,
    price,
    originalPrice,
    listingId,
    sku,
    productId,
    brandName,
    discountRate,
    categorizedLabels,
    customerReviewCount,
    customerReviewScore,
    index,
    title,
    productUrl,
    placementId,
    reasonDetailsMain,
    reasonCodeMain,
    isAdProduct,
    adInfo,
    dataTestId,
    ariaHidden,
    product,
    isBot
  } = props;

  const isCampaignTextVisible =
    categorizedLabels?.valueAddedService?.[0] &&
    discountRate > 0 &&
    categorizedLabels?.campaign?.[0];

  const incentiveToBuy = categorizedLabels?.incentiveToBuy?.[0]

  const handleProductOnClick = () => {
    let data = {
      page_type: window?.hepsiBus?.parsers?.getPageType(),
      page_value: window?.hepsiBus?.parsers?.getPageValue(),
      placement_id: placementId,
      placement_title: title,
      products: [{
        customerReviewCount: customerReviewCount,
        customerReviewScore: customerReviewScore,
        listing_id: listingId || '',
        product_id: productId || '',
        position: index + 1,
        product_status: generateProductStatus(product),
        price: originalPrice?.value?.toFixed(2),
        sku: sku || '',
        labels: ['incentiveToBuy', 'valueAddedService', 'campaign'].map(labelName => {
          const labels = categorizedLabels?.[labelName]
          const firstLabel = labels?.[0]

          return {
            type: labelName,
            id: firstLabel?.tagName || null,
            displayName: firstLabel?.imageUrl || firstLabel?.text || null
          }
        })
      }],
      recoRatio: '3:4',
      main_product: {},
      reasonCode: reasonCodeMain,
      reasonDetails: reasonDetailsMain
    }
    if (isAdProduct) {
      data.products[0].adcampaignid = adInfo?.campaignId
      data.products[0].adshowid = adInfo?.adShowId
      window?.hepsiBus?.track(DAVINCI_EVENT_NAMES.SPONSORED_PRODUCT_CLICK, data)
      return
    }
    window?.hepsiBus?.track(DAVINCI_EVENT_NAMES.RECO_PRODUCTS_CLICK, data)
  }

  return (
    <div
      data-test-id={dataTestId}
      aria-hidden={ariaHidden}
      className={cx(s.productCard, isCampaignTextVisible ? s.withCampaignText : null)}
      onClick={handleProductOnClick}>
      <div className={s.heart} role='button' aria-label='Favorilere ekle'>
        <Heart sku={sku} productId={productId} listingId={listingId} position={index} />
      </div>
      <a href={generateUrlForGoogleBot({ url: productUrl, isBot })} title={`${name} - ${price?.displayValue} ${price?.currency}`}>
        <ProductImage imageUrl={imageUrl} incentiveToBuy={incentiveToBuy} isAdProduct={isAdProduct} isBot={isBot} />
        <ProductInfo
          name={name}
          brandName={brandName}
          price={price}
          originalPrice={originalPrice}
          discountRate={discountRate}
          customerReviewCount={customerReviewCount}
          categorizedLabels={categorizedLabels}
          listingId={listingId}
          sku={sku}
          {...props}/>
      </a>
    </div>
  );
}

ProductCard.propTypes = {
  imageUrl: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  price: PropTypes.shape({
    value: PropTypes.number,
    currency: PropTypes.string
  }).isRequired,
  originalPrice: PropTypes.shape({
    value: PropTypes.number,
    currency: PropTypes.string
  }),
  customerReviewScore: PropTypes.number,
  reviews: PropTypes.array,
  brandName: PropTypes.string,
  campaignText: PropTypes.string,
  discountRate: PropTypes.number,
  inSummaryBadge: PropTypes.bool,
  imageBadge: PropTypes.string,
  inSummaryBadgeColor: PropTypes.string,
  categorizedLabels: PropTypes.shape({
    valueAddedService: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string,
        color: PropTypes.string,
        textColor: PropTypes.string
      })
    ),
    incentiveToBuy: PropTypes.arrayOf(
      PropTypes.shape({
        badgeImageUrl: PropTypes.string,
        tagName: PropTypes.string
      })
    ),
    campaign: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string
      })
    )
  }),
  customerReviewCount: PropTypes.number
};

export default memo(ProductCard)
